import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../../utils/api';

const ShippingCalculator = ({ 
  state, 
  city = '',
  address = '',
  postalCode = '',
  subtotal = 0, 
  onShippingCalculated,
  showPreview = true,
  className = '',
  storeSlug
}) => {
  console.log('ShippingCalculator: Component rendered with props:', {
    state,
    subtotal,
    showPreview,
    className,
    storeSlug
  });
  
  const [shippingInfo, setShippingInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);
  const isCalculatingRef = useRef(false);
  const lastCalculatedRef = useRef({ state: null, subtotal: null, city: null, address: null, postalCode: null, storeSlug: null });

  const calculateShipping = useCallback(async (stateValue, orderSubtotal) => {
    console.log('ShippingCalculator: calculateShipping called with state:', stateValue, 'subtotal:', orderSubtotal);
    console.log('ShippingCalculator: Current lastCalculatedRef:', lastCalculatedRef.current);
    if (!stateValue) {
      console.log('ShippingCalculator: No state provided, returning');
      return;
    }

    // Check if we already calculated for these exact values
    const resolvedStoreSlug = (storeSlug && storeSlug.trim())
      || new URLSearchParams(window.location.search).get('store')
      || (window.location.pathname.match(/\/s\/([^/]+)/)?.[1] || '');

    if (
      lastCalculatedRef.current.state === stateValue &&
      lastCalculatedRef.current.subtotal === orderSubtotal &&
      lastCalculatedRef.current.city === city &&
      lastCalculatedRef.current.address === address &&
      lastCalculatedRef.current.postalCode === postalCode &&
      lastCalculatedRef.current.storeSlug === resolvedStoreSlug
    ) {
      console.log('ShippingCalculator: Already calculated for these values, returning');
      return;
    }

    // Prevent multiple simultaneous calls
    if (isCalculatingRef.current) {
      console.log('ShippingCalculator: Already calculating, returning');
      return;
    }
    
    isCalculatingRef.current = true;

    try {
      setLoading(true);
      setError(null);

      // Resolve store slug from prop, query param, or URL path (/s/:slug)
      console.log('ShippingCalculator: Starting calculation for state:', stateValue, 'subtotal:', orderSubtotal, 'storeSlug:', resolvedStoreSlug);

      if (!resolvedStoreSlug) {
        console.error('ShippingCalculator: Missing store slug');
        setError('Missing store information');
        setShippingInfo(null);
        return;
      }

      const response = await apiFetch('/shipping-zones/calculate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          state: stateValue, 
          city,
          address,
          postalCode,
          orderSubtotal: orderSubtotal,
          storeSlug: resolvedStoreSlug
        })
      });

      console.log('Shipping response status:', response.status);
      console.log('Shipping response headers:', response.headers);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Shipping API error response:', errorText);
        throw new Error(`Failed to calculate shipping: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      console.log('Shipping data:', data);
      console.log('Shipping data details:', {
        state: data.state,
        shippingCost: data.shippingCost,
        finalShippingCost: data.finalShippingCost,
        isFreeShipping: data.isFreeShipping,
        freeShippingThreshold: data.freeShippingThreshold
      });
      
      // Update the last calculated values
      lastCalculatedRef.current = {
        state: stateValue,
        subtotal: orderSubtotal,
        city,
        address,
        postalCode,
        storeSlug: resolvedStoreSlug
      };
      
      console.log('ShippingCalculator: Setting shipping info:', data);
      setShippingInfo(data);
      
      if (onShippingCalculated) {
        console.log('ShippingCalculator: Calling onShippingCalculated with:', data);
        onShippingCalculated(data);
      }
    } catch (err) {
      console.error('Shipping calculation error:', err);
      setError(err.message);
      setShippingInfo(null);
    } finally {
      setLoading(false);
      isCalculatingRef.current = false;
    }
  }, [storeSlug, onShippingCalculated, city, address, postalCode]);

  useEffect(() => {
    console.log('ShippingCalculator: useEffect triggered with state:', state, 'subtotal:', subtotal);
    console.log('ShippingCalculator: Previous values:', lastCalculatedRef.current);
    if (state && subtotal >= 0) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Only calculate if values have actually changed
      if (
        lastCalculatedRef.current.state !== state ||
        lastCalculatedRef.current.subtotal !== subtotal ||
        lastCalculatedRef.current.city !== city ||
        lastCalculatedRef.current.address !== address ||
        lastCalculatedRef.current.postalCode !== postalCode ||
        lastCalculatedRef.current.storeSlug !== ((storeSlug && storeSlug.trim()) || new URLSearchParams(window.location.search).get('store') || (window.location.pathname.match(/\/s\/([^/]+)/)?.[1] || ''))
      ) {
        
        console.log('ShippingCalculator: Values changed, scheduling calculation for state:', state, 'subtotal:', subtotal);
        // Add a delay to prevent rapid calls and flickering
        timeoutRef.current = setTimeout(() => {
          calculateShipping(state, subtotal);
        }, 500); // Reduced delay to 500ms for faster response
      } else {
        console.log('ShippingCalculator: Skipping calculation - values unchanged');
      }
      
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    } else {
      console.log('ShippingCalculator: Not calculating - missing state or subtotal');
    }
  }, [state, subtotal, city, address, postalCode, storeSlug, calculateShipping]);

  if (!showPreview || !state) {
    return null;
  }

  if (loading) {
    return (
      <div className={`shipping-calculator ${className}`} style={{
        padding: '16px',
        backgroundColor: '#F9FAFB',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        width: '100%',
        minHeight: '80px', // Fixed height to prevent layout shift
        display: 'flex',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid #3B82F6',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>
            Calculating shipping costs...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`shipping-calculator ${className}`} style={{
        padding: '16px',
        backgroundColor: '#FEF2F2',
        borderRadius: '8px',
        border: '1px solid #FECACA',
        width: '100%'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '18px' }}>⚠️</span>
          <span style={{ fontSize: '14px', color: '#DC2626', fontWeight: '500' }}>
            Unable to calculate shipping costs
          </span>
        </div>
      </div>
    );
  }

  if (!shippingInfo) {
    return null;
  }

  return (
    <div className={`shipping-calculator ${className}`} style={{
      padding: 'var(--space-4)',
      backgroundColor: '#F0FDF4',
      borderRadius: 'var(--radius-md)',
      border: '1px solid #BBF7D0',
      width: '100%',
      minHeight: '80px' // Fixed height to prevent layout shift
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
        <span style={{ fontSize: '20px' }}>🚚</span>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: '16px', fontWeight: '600', color: '#166534' }}>
            Shipping to {state}
          </span>
          {shippingInfo.zoneName && (
            <span style={{ fontSize: '12px', color: '#065F46' }}>
              Zone: {shippingInfo.zoneName}
            </span>
          )}
        </div>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>
            ₦{(shippingInfo.shippingCost || 0).toLocaleString('en-NG')}
          </div>
          <div style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>
            {Math.max(1, Number(shippingInfo.estimatedDeliveryDays || 1))} day
            {Math.max(1, Number(shippingInfo.estimatedDeliveryDays || 1)) !== 1 ? 's' : ''} delivery
          </div>
        </div>
        
        {shippingInfo.freeShippingThreshold > 0 && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>
              Free shipping over ₦{shippingInfo.freeShippingThreshold.toLocaleString('en-NG')}
            </div>
            {subtotal < shippingInfo.freeShippingThreshold && (
              <div style={{ fontSize: '14px', color: '#059669', fontWeight: '600' }}>
                Add ₦{Math.max(0, shippingInfo.freeShippingThreshold - subtotal).toLocaleString('en-NG')} more
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShippingCalculator;
